import { useEffect, useState } from "react";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { formatPrice, getProduct, products, statusLabels, type Product } from "@/data/products";
import { faqs } from "@/data/content";
import { site, pageUrl } from "@/data/site";
import { Breadcrumbs, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ProductGrid } from "@/components/site/product-grid";
import { ProductStatusBadge } from "@/components/site/product-card";
import { FAQAccordion } from "@/components/site/faq-accordion";
import { FinalCTA } from "@/components/site/final-cta";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/creations/$slug")({
  loader: ({ params }) => {
    const product = getProduct(params.slug);
    if (!product) throw notFound();
    return { product };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Creation not found | Paul's Pens n Things" }, { name: "robots", content: "noindex" }] };
    }
    const p = loaderData.product;
    return {
      meta: [
        { title: p.seoTitle },
        { name: "description", content: p.seoDescription },
        { property: "og:title", content: p.name },
        { property: "og:description", content: p.seoDescription },
        { property: "og:type", content: "product" },
        { property: "og:url", content: pageUrl(`/creations/${params.slug}`) },
      ],
      links: [{ rel: "canonical", href: pageUrl(`/creations/${params.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: pageUrl("/") },
              { "@type": "ListItem", position: 2, name: "All Creations", item: pageUrl("/gallery") },
              { "@type": "ListItem", position: 3, name: p.name },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.name,
            sku: p.internalCode,
            description: p.seoDescription,
            image: p.images.map((img) => img.src),
            brand: { "@type": "Brand", name: site.name },
            ...(p.price !== null
              ? {
                  offers: {
                    "@type": "Offer",
                    price: p.price.toFixed(2),
                    priceCurrency: "USD",
                    availability:
                      p.status === "available"
                        ? "https://schema.org/InStock"
                        : p.status === "made-to-order"
                          ? "https://schema.org/PreOrder"
                          : "https://schema.org/OutOfStock",
                    seller: { "@type": "Organization", name: site.name },
                  },
                }
              : {}),
          }),
        },
      ],
    };
  },
  component: CreationDetail,
  notFoundComponent: () => {
    const { slug } = Route.useParams();
    return (
      <div className="shell py-24">
        <h1 className="display-lg">That creation is not on record</h1>
        <p className="lede mt-4">
          Nothing is filed under &ldquo;{slug}&rdquo;. Browse the gallery, or describe what you were hoping to
          find and Paul will tell you whether he can make it.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <CtaLink to="/gallery" className="h-12">
            Browse all creations
          </CtaLink>
          <CtaLink to="/design-your-piece" variant="outline" className="h-12">
            Design your piece
          </CtaLink>
        </div>
      </div>
    );
  },
  errorComponent: () => (
    <div className="shell py-24">
      <h1 className="display-lg">This creation didn&apos;t load</h1>
      <p className="lede mt-4">Please try again, or email {site.email}.</p>
    </div>
  ),
});

function CreationDetail() {
  const { product } = Route.useLoaderData() as { product: Product };
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const image = product.images[index];
  const related = product.relatedProducts
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  useEffect(() => {
    track("view_product", { item_id: product.slug, category: product.category });
    setIndex(0);
  }, [product.slug, product.category]);

  return (
    <>
      <Breadcrumbs
        trail={[
          { label: "Home", to: "/" },
          { label: "All Creations", to: "/gallery" },
          { label: product.name },
        ]}
      />

      <div className="shell grid gap-12 py-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        <div>
          <button
            type="button"
            onClick={() => setZoomed((z) => !z)}
            aria-label={zoomed ? "Zoom out of product image" : "Zoom into product image"}
            className="block w-full overflow-hidden bg-paper"
          >
            <img
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              fetchPriority="high"
              decoding="async"
              className={cn(
                "aspect-[4/3] w-full object-cover motion-craft transition-transform duration-500",
                zoomed && "scale-150",
              )}
            />
          </button>
          {product.images.length > 1 && (
            <ul className="mt-3 flex gap-3 overflow-x-auto pb-1">
              {product.images.map((im, i) => (
                <li key={im.src + i}>
                  <button
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Show image ${i + 1} of ${product.images.length}`}
                    aria-current={i === index}
                    className={cn(
                      "block size-20 overflow-hidden border",
                      i === index ? "border-brass" : "border-hairline",
                    )}
                  >
                    <img src={im.src} alt="" width={160} height={160} className="size-full object-cover" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Tap or click the photograph to zoom. Photographs show a previous build; your piece will vary with
            the material chosen.
          </p>
        </div>

        <div>
          <p className="eyebrow">{product.subcategory}</p>
          <h1 className="display-lg mt-3">{product.name}</h1>
          <p className="mt-3 text-xs uppercase tracking-[0.14em] text-muted-foreground">
            Reference {product.internalCode}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <ProductStatusBadge status={product.status} />
            <p className="font-[family-name:var(--font-display)] text-2xl">{formatPrice(product)}</p>
          </div>

          <p className="lede mt-6">{product.shortDescription}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/design-your-piece"
              search={{ ref: product.slug }}
              onClick={() => track("request_similar_click", { item_id: product.slug })}
              className="tap-safe inline-flex h-12 items-center justify-center rounded-sm bg-ink px-6 text-sm font-semibold text-background motion-craft transition-colors hover:bg-walnut"
            >
              {product.status === "previously-created" ? "Request Something Similar" : "Request This Piece"}
            </Link>
            <a
              href={`mailto:${site.email}?subject=${encodeURIComponent(`Question about ${product.name} (${product.internalCode})`)}`}
              onClick={() => track("click_email", { location: "product_detail" })}
              className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/25 px-6 text-sm font-semibold hover:bg-paper motion-craft transition-colors"
            >
              Ask Paul a question
            </a>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Paul confirms the design, materials and price with you before sending a PayPal invoice. Nothing is
            charged until you agree.
          </p>

          <div className="mt-10 border-t border-hairline pt-8">
            <h2 className="text-base font-semibold">About this piece</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{product.fullDescription}</p>
            {product.story && (
              <blockquote className="mt-6 border-l-2 border-brass pl-4 text-sm italic leading-relaxed text-ink/80">
                {product.story}
              </blockquote>
            )}
          </div>

          <div className="mt-10 border-t border-hairline pt-8">
            <h2 className="text-base font-semibold">Specifications</h2>
            <dl className="mt-4 divide-y divide-hairline border-y border-hairline text-sm">
              <Spec label="Materials" value={product.materials.join(", ")} />
              <Spec label="Finishes" value={product.finishes.join(", ")} />
              {product.dimensions && <Spec label="Dimensions" value={product.dimensions} />}
              <Spec label="Status" value={statusLabels[product.status]} />
              <Spec
                label="Customization"
                value={product.customizable ? "Materials, finish and personalization can be changed" : "Supplied as described"}
              />
              <Spec label="Turnaround" value={product.turnaround ?? "Confirmed with Paul for each project"} />
              <Spec
                label="Shipping"
                value={product.shippingNotes ?? "Arranged with Paul when your order is confirmed"}
              />
            </dl>
          </div>

          {product.occasions.length > 0 && (
            <div className="mt-8">
              <h2 className="text-base font-semibold">Often given for</h2>
              <ul className="mt-3 flex flex-wrap gap-2">
                {product.occasions.map((o) => (
                  <li key={o} className="rounded-full border border-ink/15 px-3 py-1.5 text-xs text-ink/75">
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <Section tone="paper" labelledBy="related-heading">
          <div className="shell">
            <SectionHeading
              id="related-heading"
              eyebrow="Related creations"
              title="You might also look at these."
              action={
                <Link to="/gallery" className="text-sm font-semibold text-walnut underline underline-offset-4">
                  View all creations
                </Link>
              }
            />
            <ProductGrid products={related} />
          </div>
        </Section>
      )}

      <Section labelledBy="product-faq-heading">
        <div className="shell grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <SectionHeading id="product-faq-heading" eyebrow="Questions" title="Before you request it." />
          <FAQAccordion items={faqs.slice(0, 4)} />
        </div>
      </Section>

      <FinalCTA />
    </>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-4 py-3">
      <dt className="font-semibold">{label}</dt>
      <dd className="text-muted-foreground">{value}</dd>
    </div>
  );
}
